import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import BrandMark from "./BrandMark";

const NAV = [
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/pricing" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Help", to: "/help" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar({ onBookDemo }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#030712]/80 backdrop-blur-2xl border-b border-white/[0.08]"
          : "bg-transparent"
      }`}
      data-testid="mkt-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <BrandMark size="md" />

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => {
            const active =
              pathname === n.to || (n.to !== "/" && pathname.startsWith(n.to));
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  active
                    ? "text-white bg-white/[0.06]"
                    : "text-slate-400 hover:text-white"
                }`}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
              >
                {n.label}
              </Link>
            );
          })}
          {/* Highlighted free-trial CTA right after Contact */}
          <Link
            to="/signup"
            className="ml-2 px-4 py-2 text-sm font-bold rounded-full text-white transition-transform hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)",
              boxShadow: "0 10px 24px -8px rgba(236,72,153,0.6)",
            }}
            data-testid="nav-try-free"
          >
            Try it Free
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {/* Always-visible Call CTA so buyers can dial us in one tap */}
          <a
            href="tel:+919464645491"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold text-white transition-all hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #16a34a 0%, #059669 100%)",
              boxShadow: "0 8px 20px -6px rgba(5,150,105,0.55)",
            }}
            data-testid="nav-call-us"
          >
            <Phone size={12} /> +91 94646 45491
          </a>
          <Link
            to="/login"
            className="text-sm font-medium text-slate-300 hover:text-white px-3 py-2"
            data-testid="nav-sign-in"
          >
            Sign in
          </Link>
          <Link
            to="/brands"
            className="mkt-btn-gradient text-sm"
            data-testid="nav-brandsbyindia"
          >
            BrandsByIndia <ArrowUpRight size={14} />
          </Link>
        </div>

        <button
          className="md:hidden p-2 text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-white/[0.08] bg-[#030712]/95 backdrop-blur-2xl">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="px-4 py-3 text-base text-slate-200 hover:bg-white/[0.04] rounded-xl"
                data-testid={`nav-mobile-${n.label.toLowerCase()}`}
              >
                {n.label}
              </Link>
            ))}
            <div className="h-px bg-white/[0.08] my-2" />
            <a
              href="tel:+919464645491"
              className="px-4 py-3 text-base font-bold text-white rounded-xl text-center inline-flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #16a34a 0%, #059669 100%)" }}
              data-testid="nav-mobile-call-us"
            >
              <Phone size={14} /> Call +91 94646 45491
            </a>
            <Link
              to="/signup"
              className="px-4 py-3 text-base font-bold text-white rounded-xl text-center"
              style={{
                background: "linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)",
              }}
              data-testid="nav-mobile-try-free"
            >
              Try it Free — 1 listing
            </Link>
            <Link
              to="/login"
              className="px-4 py-3 text-base text-slate-300 hover:bg-white/[0.04] rounded-xl"
            >
              Sign in
            </Link>
            <Link
              to="/brands"
              className="mkt-btn-gradient w-full justify-center mt-2"
              data-testid="nav-mobile-brandsbyindia"
            >
              BrandsByIndia <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
